import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputLabel,
    ListItemText,
    Paper,
    Stack,
    Typography,
    capitalize,
    styled,
    useMediaQuery,
} from '@mui/material';
import { ModalProps } from '../../../types/modal';
import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import FormAutocomplete from '../../../shared/FormAutocomplete';
import FormTextField from '../../../shared/FormTextField';
import FormMultiAutocomplete from '../../../shared/FormMultiAutocomplete';
import UserRole from '../../../../shared/AuthRoles';
import {
    useCreateJobRequestMutation,
    useGetAllReservationsQuery,
    useGetOwnerContractorsQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from '../../../shared/Toast';
import EngineeringIcon from '@mui/icons-material/Engineering';
import { Add, Close } from '@mui/icons-material';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import FormDateFilter from '../../../shared/FormDateFilter';
import { camelCaseToTitleCase } from '../../../../shared/utils';
import { Contractor } from '../../../types/contractor';
import { getUnavailableDays } from '../../Profile/Form/Contractor/Availability';
import { endOfDay, isWithinInterval, startOfDay } from 'date-fns';
import dayjs from 'dayjs';
import { Period } from '../../../types/contractor';
import ContractorJobPolicies from './ContractorJobPolicies';
import { theme } from '../../../../shared/theme';
import { useNavigate } from 'react-router-dom';
import { ListBooking } from '../../../types/reservation';
import { getAvailableTimes } from '../../Contractors/utils/availability.utils';
import {
    getContractorBusinessDayNumbers,
    isBeforeJobRequestCutoff,
} from '../../Contractors/utils/cutoff.utils';

type DisabledDays = {
    unavailablePeriods: Period[];
    unavailableDays: number[];
};

export const shouldDisableDate = (
    date: dayjs.Dayjs,
    disabledDays: DisabledDays,
    cutoffDays?: number | null,
    businessDayNumbers?: number[],
) => {
    const day = startOfDay(date.toDate());
    for (const period of disabledDays.unavailablePeriods) {
        const start = startOfDay(new Date(period.from));
        const end = endOfDay(new Date(period.to));
        if (isWithinInterval(day, { start, end })) return true;
    }
    if (disabledDays.unavailableDays.includes(date.day())) return true;
    if (
        isBeforeJobRequestCutoff(date.toDate(), cutoffDays, businessDayNumbers)
    ) {
        return true;
    }
    return false;
};

export const getDisabledDateMessage = (
    date: dayjs.Dayjs,
    disabledDays: DisabledDays,
    cutoffDays?: number | null,
    businessDayNumbers?: number[],
) => {
    const day = startOfDay(date.toDate());

    for (const period of disabledDays.unavailablePeriods) {
        const start = startOfDay(new Date(period.from));
        const end = endOfDay(new Date(period.to));

        if (isWithinInterval(day, { start, end })) {
            const from = dayjs(period.from);
            const to = dayjs(period.to);
            const sameDay = from.isSame(to, 'day');

            return sameDay
                ? `Contractor is unavailable on (${from.format('DD/MM/YYYY')})`
                : `Contractor is unavailable (${from.format('DD/MM/YYYY')} - ${to.format(
                      'DD/MM/YYYY',
                  )})`;
        }
    }

    if (disabledDays.unavailableDays.includes(date.day())) {
        return `Contractor is unavailable on ${date.format('dddd')}s`;
    }

    if (
        cutoffDays != null &&
        cutoffDays >= 0 &&
        isBeforeJobRequestCutoff(date.toDate(), cutoffDays, businessDayNumbers)
    ) {
        return `This contractor requires job requests to be submitted at least ${cutoffDays} business day/s before the job start date`;
    }

    return undefined;
};

const Schema = z
    .object({
        booking: z.object({ id: z.number() }),
        contractor: z.object({
            id: z.coerce.number().int().positive({ message: 'Required' }),
        }),
        hasCancellationPolicy: z.boolean().default(false),
        jobDate: z.coerce.date(),
        ownerNotes: z.string().default(''),
        services: z
            .array(z.string())
            .min(1, { message: 'At least 1 service required' }),
        templateId: z.coerce.number().optional(),
        items: z.array(z.any()).default([]),
        guestNotes: z.string().default(''),
        startTime: z.string().min(1, { message: 'Required' }),
        endTime: z.string().min(1, { message: 'Required' }),
        createdBy: z.string().default('owner'),
        policy: z.object({ checked: z.boolean() }).optional(),
    })
    .superRefine((values, ctx) => {
        if (!values.hasCancellationPolicy) return;
        if (!values.policy?.checked) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['policy', 'checked'],
                message: 'Please accept the cancellation policy to continue.',
            });
        }
    });

interface ContractorModalProps extends ModalProps {
    /** Reservation ID */
    id?: number;
    startDate?: string;
}

const CustomInputLabel = styled(InputLabel)(() => ({
    fontSize: '12px',
    lineHeight: 1.2,
    fontWeight: 700,
}));

const ContractorModal = ({
    id,
    startDate,
    open,
    onClose,
}: ContractorModalProps) => {
    const loggedInUser = UserRole();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const reservationId = typeof id === 'number' && id > 0 ? id : undefined;

    const [createJob] = useCreateJobRequestMutation();
    const { data: allReservations } = useGetAllReservationsQuery({
        status: ['pending', 'approved', 'completed'],
        bookerType: 'guest',
        sort: 'startDate',
    });

    const { data: contractors } = useGetOwnerContractorsQuery(
        loggedInUser.company,
        { skip: !loggedInUser?.company },
    );

    const reservationOptions = useMemo(() => {
        const base =
            allReservations?.map((res: ListBooking) => ({
                value: res.id,
                label: `Reservation #${res.id} - ${res.module?.name} - ${
                    res.guest?.name || res.owner?.name || '—'
                } (${res.status})`,
            })) || [];

        if (!reservationId) return base;

        const hasIt = base.some(
            (o: { value: number }) => o.value === reservationId,
        );
        if (hasIt) return base;

        return [
            {
                value: reservationId,
                label: `Reservation #${reservationId} (Owner/Blocked)`,
            },
            ...base,
        ];
    }, [allReservations, reservationId]);

    type Job = z.infer<typeof Schema>;

    const methods = useForm<Job>({
        defaultValues: {
            policy: { checked: false },
            booking: { id: reservationId },
            contractor: { id: -1 },
            hasCancellationPolicy: false,
            jobDate: startDate ? new Date(startDate) : undefined,
            ownerNotes: '',
            services: [],
            items: [],
            guestNotes: '',
            startTime: '',
            endTime: '',
        },
        resolver: zodResolver(Schema),
        mode: 'onChange',
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { isSubmitting, isValid },
    } = methods;

    const bookingId = useWatch({ name: 'booking.id', control });
    const selectedContractor = useWatch({ name: 'contractor', control });
    const selectedDate = useWatch({ name: 'jobDate', control });
    const startTime = useWatch({ name: 'startTime', control });
    const endTime = useWatch({ name: 'endTime', control });

    useEffect(() => {
        if (!open) return;
        if (!reservationId) return;
        if (!reservationOptions.length) return;

        const hasOption = reservationOptions.some(
            (o: any) => o.value === reservationId,
        );
        if (!hasOption) return;

        if (bookingId !== reservationId) {
            methods.setValue('booking.id', reservationId, {
                shouldValidate: true,
                shouldDirty: false,
                shouldTouch: false,
            });
        }

        methods.trigger('booking.id');
    }, [open, reservationId, reservationOptions, bookingId, methods]);

    const contractor = contractors?.find((c) => c.id === selectedContractor.id);
    const businessDayNumbers = getContractorBusinessDayNumbers(
        contractor?.availableDays,
    );

    const { startTimes, endTimes } = getAvailableTimes(
        contractor,
        selectedDate,
        startTime,
        endTime,
    );

    const disabledDays = useMemo(() => {
        const unavailablePeriods = contractor?.unavailablePeriods || [];
        const availableDays = contractor?.availableDays || [];
        const unavailableDays = getUnavailableDays(
            availableDays.map((availability) => availability.day),
        );
        return { unavailablePeriods, unavailableDays };
    }, [contractor]);

    const availableServices = contractor?.services.items;

    useEffect(() => {
        methods.setValue(
            'hasCancellationPolicy',
            Boolean(contractor?.cancellationPolicy),
            { shouldValidate: true },
        );
    }, [methods, contractor]);

    const handleClose = () => {
        onClose();
        reset();
    };

    const onSubmit = async (data: Job) => {
        try {
            const payload = {
                ...data,
                jobDate: dayjs(data.jobDate).format('YYYY-MM-DD'),
            };
            await createJob(payload).unwrap();
            reset();
            handleClose();
            toast.success('Job request sent successfully');
        } catch {
            toast.error('Error sending job request');
        }
    };

    const contractorOptions =
        contractors?.map((c) => ({
            value: c.id,
            label: c.name,
            ...c,
        })) || [];

    const openCreateContractor = () => {
        handleClose();
        navigate('/contractors', { state: { openCreateContractor: true } });
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullScreen={isMobile}
            maxWidth='lg'
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 3,
                    overflow: 'hidden',
                    border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                    display: 'flex',
                    flexDirection: 'column',
                    height: isMobile ? '100dvh' : 'auto',
                },
            }}
        >
            <FormProvider {...methods}>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: isMobile ? '100%' : 'auto',
                    }}
                >
                    <DialogTitle
                        sx={{
                            position: isMobile ? 'sticky' : 'relative',
                            top: 0,
                            zIndex: 2,
                        }}
                    >
                        <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    minWidth: 0,
                                }}
                            >
                                <EngineeringIcon
                                    sx={{
                                        fontSize: '20px',
                                        color: 'primary',
                                        opacity: 0.75,
                                        flexShrink: 0,
                                    }}
                                />
                                <Box
                                    sx={{
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    Job Request
                                </Box>
                            </Box>

                            <Typography
                                sx={{
                                    fontSize: 11,
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                Note: A job request will be submitted to the
                                selected contractor where they will approve or
                                decline the job.
                            </Typography>
                        </Stack>

                        <IconButton
                            onClick={handleClose}
                            sx={{ color: 'text.primary', mt: -0.25 }}
                            aria-label='close'
                        >
                            <Close />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            WebkitOverflowScrolling: 'touch',
                        }}
                    >
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={6}>
                                <CustomInputLabel
                                    required
                                    htmlFor='reservation-id__input'
                                >
                                    Reservation
                                </CustomInputLabel>
                                <FormAutocomplete
                                    id='reservation-id__input'
                                    key={`${reservationId || 'select'}-${reservationOptions.length}`}
                                    disabled={Boolean(reservationId)}
                                    name='booking.id'
                                    control={control}
                                    options={reservationOptions}
                                    placeholder='Select experience'
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <CustomInputLabel
                                    required
                                    htmlFor='contractor-id__input'
                                >
                                    Contractor
                                </CustomInputLabel>
                                <FormAutocomplete
                                    id='contractor-id__input'
                                    name='contractor.id'
                                    control={control}
                                    options={contractorOptions}
                                    noOptionsText='No contractors found'
                                    PaperComponent={({ children }) => (
                                        <Paper>
                                            <Button
                                                fullWidth
                                                size='large'
                                                sx={{
                                                    justifyContent:
                                                        'flex-start',
                                                    pl: 2,
                                                    mt: 1,
                                                }}
                                                startIcon={<Add />}
                                                onMouseDown={(e) => {
                                                    e.stopPropagation();
                                                    openCreateContractor();
                                                }}
                                            >
                                                Create new contractor
                                            </Button>
                                            {children}
                                        </Paper>
                                    )}
                                    renderOption={(props, option) => (
                                        <li key={option.value} {...props}>
                                            <ListItemText
                                                primary={option.label}
                                                secondary={
                                                    option.services &&
                                                    capitalize(
                                                        option.services.type,
                                                    )
                                                }
                                            />
                                        </li>
                                    )}
                                />
                            </Grid>
                            <Grid item container spacing={1}>
                                <Grid item xs={12} md={6}>
                                    <CustomInputLabel
                                        required
                                        htmlFor='job-date__input'
                                    >
                                        Date
                                    </CustomInputLabel>
                                    <FormDateFilter
                                        id='job-date__input'
                                        name='jobDate'
                                        control={control}
                                        defaultCalendarMonth={dayjs(startDate)}
                                        shouldDisableDate={(date) =>
                                            shouldDisableDate(
                                                date,
                                                disabledDays,
                                                contractor?.cutoff,
                                                businessDayNumbers,
                                            )
                                        }
                                        getDisabledDateMessage={(date) =>
                                            getDisabledDateMessage(
                                                date,
                                                disabledDays,
                                                contractor?.cutoff,
                                                businessDayNumbers,
                                            )
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <CustomInputLabel
                                        required
                                        htmlFor='services__input'
                                    >
                                        Services
                                    </CustomInputLabel>
                                    <FormMultiAutocomplete
                                        id='services__input'
                                        name='services'
                                        control={control}
                                        fullWidth
                                        disabled={!availableServices}
                                        placeholder={
                                            availableServices
                                                ? ''
                                                : 'Select contractor'
                                        }
                                        options={
                                            availableServices?.map(
                                                (service: string) => ({
                                                    value: service,
                                                    label: camelCaseToTitleCase(
                                                        service,
                                                    ),
                                                }),
                                            ) || []
                                        }
                                    />
                                </Grid>
                            </Grid>

                            <Grid item container spacing={1}>
                                <Grid item xs={12} md={6}>
                                    <CustomInputLabel
                                        required
                                        htmlFor='start-time__input'
                                    >
                                        Start Time
                                    </CustomInputLabel>
                                    <FormAutocomplete
                                        id='start-time__input'
                                        name='startTime'
                                        control={control}
                                        options={startTimes}
                                        disabled={!contractor || !selectedDate}
                                        placeholder={
                                            !contractor
                                                ? 'Select contractor'
                                                : !selectedDate
                                                  ? 'Select job date'
                                                  : ''
                                        }
                                        disableClearable={false}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <CustomInputLabel
                                        required
                                        htmlFor='end-time__input'
                                    >
                                        End Time
                                    </CustomInputLabel>
                                    <FormAutocomplete
                                        id='end-time__input'
                                        name='endTime'
                                        control={control}
                                        options={endTimes}
                                        disabled={!contractor || !selectedDate}
                                        placeholder={
                                            !contractor
                                                ? 'Select contractor'
                                                : !selectedDate
                                                  ? 'Select job date'
                                                  : ''
                                        }
                                        disableClearable={false}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <CustomInputLabel
                                    required
                                    htmlFor='owner-notes__input'
                                >
                                    Notes
                                </CustomInputLabel>
                                <FormTextField
                                    id='owner-notes__Input'
                                    name='ownerNotes'
                                    control={control}
                                    multiline
                                    rows={4}
                                />
                            </Grid>
                            {contractor && contractor?.cancellationPolicy && (
                                <Grid item xs={12} sx={{ mt: 2 }}>
                                    <ContractorJobPolicies
                                        contractor={contractor as Contractor}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </DialogContent>

                    <DialogActions
                        sx={{
                            position: isMobile ? 'sticky' : 'relative',
                            bottom: 0,
                            zIndex: 2,
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                gap: 1,
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Button
                                variant='outlined'
                                color='primary'
                                onClick={handleClose}
                                sx={{
                                    minHeight: 44,
                                    ...(isMobile ? { flex: 1 } : {}),
                                }}
                            >
                                Cancel
                            </Button>

                            <CustomButton
                                variant='contained'
                                color='primary'
                                type='submit'
                                disabled={isSubmitting || !isValid}
                                sx={{
                                    minHeight: 44,
                                    ...(isMobile ? { flex: 1 } : {}),
                                }}
                            >
                                Request
                            </CustomButton>
                        </Box>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};

export default ContractorModal;
